import React from 'react';

const styles = {
  card: {
    background: 'var(--bg-elevated)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-light)',
    boxShadow: 'var(--shadow-lg)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
  },
  cardHover: {
    transform: 'translateY(-4px)',
    boxShadow: 'var(--shadow-2xl)',
    borderColor: 'var(--gold)',
  },
  cardPadding: {
    sm: 'var(--space-6)',
    md: 'var(--space-8)',
    lg: 'var(--space-10)',
    xl: 'var(--space-12)',
  },
  luxuryAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'var(--gradient-gold)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  luxuryAccentVisible: {
    opacity: 1,
  },
  header: {
    padding: 'var(--space-8)',
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
  },
  headerTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-2xl)',
    fontWeight: 700,
    color: 'var(--text)',
    margin: 0,
    letterSpacing: 'var(--tracking-tight)',
  },
  headerSubtitle: {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    color: 'var(--text-tertiary)',
    marginTop: '0.5rem',
    letterSpacing: 'var(--tracking-wide)',
  },
  body: {
    position: 'relative',
  },
  footer: {
    padding: 'var(--space-6) var(--space-8)',
    borderTop: '1px solid var(--border)',
    background: 'var(--bg-secondary)',
  },
  glassMorphism: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  goldShimmer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at var(--shimmer-x, 50%) var(--shimmer-y, 50%), var(--gold-subtle) 0%, transparent 50%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    pointerEvents: 'none',
  },
  goldShimmerVisible: {
    opacity: 0.3,
  },
};

export function Card({
  children,
  title,
  subtitle,
  footer,
  padding = 'md',
  hoverable = false,
  luxuryAccent = false,
  glassMorphism = false,
  goldShimmer = false,
  overflowVisible = false,
  onClick,
  style = {},
  className = '',
  ...props
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [shimmerPosition, setShimmerPosition] = React.useState({ x: 50, y: 50 });
  const cardRef = React.useRef(null);
  
  const handleMouseMove = (e) => {
    if (!goldShimmer || !cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setShimmerPosition({ x, y });
  };
  
  const hasHeader = title || subtitle;
  const paddingStyle = { padding: styles.cardPadding[padding] };
  
  const cardStyle = {
    ...styles.card,
    ...(overflowVisible ? { overflow: 'visible' } : {}),
    ...(glassMorphism ? styles.glassMorphism : {}),
    ...(hoverable && isHovered ? styles.cardHover : {}),
    ...(onClick ? { cursor: 'pointer' } : {}),
    ...style,
  };
  
  return (
    <div
      ref={cardRef}
      className={`luxury-card ${className}`}
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={handleMouseMove}
      onClick={onClick}
      {...props}
    >
      {luxuryAccent && (
        <div 
          style={{
            ...styles.luxuryAccent,
            ...(isHovered ? styles.luxuryAccentVisible : {})
          }} 
        />
      )}
      
      {hasHeader && (
        <div style={styles.header}>
          {title && <h3 style={styles.headerTitle}>{title}</h3>}
          {subtitle && <p style={styles.headerSubtitle}>{subtitle}</p>}
        </div>
      )}
      
      <div style={{ ...styles.body, ...(!hasHeader && !footer ? paddingStyle : paddingStyle) }}>
        {children}
        
        {goldShimmer && (
          <div 
            style={{
              ...styles.goldShimmer,
              ...(isHovered ? styles.goldShimmerVisible : {}),
              '--shimmer-x': `${shimmerPosition.x}%`,
              '--shimmer-y': `${shimmerPosition.y}%`,
            }} 
          />
        )}
      </div>
      
      {footer && (
        <div style={styles.footer}>
          {footer}
        </div>
      )}
    </div>
  );
}

export default Card;